import { createSelector } from 'reselect';
// Import state types.
import {
	PolicyState,
} from '@app/redux/states';
// Import api models.
import {
	PolicyNoteRecord,
} from '@app/models/api';
// Import base selectors.
import {
	getPoliciesState,
} from '@app/redux/selectors/base';
import {
	UnderwriterActionType
} from '@app/enumerations';

// Get Policy Notes Selector.
const makeGetPolicyNotesSelector = () => createSelector(
	[getPoliciesState],
	(policyState: PolicyState | undefined): PolicyNoteRecord[] => {
		return policyState !== undefined
			? policyState.policyNotes
			: [];
	}
);

// Get Underwriter Declined Policy Notes Selector.
const makeGetUnderwriterDeclinedPolicyNotesSelector = () => createSelector(
	[makeGetPolicyNotesSelector()],
	(policyNotes: PolicyNoteRecord[]): PolicyNoteRecord[] => {
		return policyNotes.filter((policyNote: PolicyNoteRecord) => policyNote.underwriterAction === UnderwriterActionType.DECLINE);
	}
);

// Get Underwriter Hold Policy Notes Selector.
const makeGetUnderwriterHoldPolicyNotesSelector = () => createSelector(
	[makeGetPolicyNotesSelector()],
	(policyNotes: PolicyNoteRecord[]): PolicyNoteRecord[] => {
		return policyNotes.filter((policyNote: PolicyNoteRecord) => policyNote.underwriterAction === UnderwriterActionType.HOLD);
	}
);

// Get Underwriter No Decision Policy Notes Selector.
const makeGetUnderwriterNoDecisionPolicyNotesSelector = () => createSelector(
	[makeGetPolicyNotesSelector()],
	(policyNotes: PolicyNoteRecord[]): PolicyNoteRecord[] => {
		return policyNotes.filter((policyNote: PolicyNoteRecord) => policyNote.underwriterAction === UnderwriterActionType.NO_DECISION);
	}
);

// Get Latest Underwriter No Decision Policy Note Selector.
const makeGetLatestUnderwriterNoDecisionPolicyNoteSelector = () => createSelector(
	[makeGetUnderwriterNoDecisionPolicyNotesSelector()],
	(policyNotes: PolicyNoteRecord[]): PolicyNoteRecord | undefined => {
		return policyNotes[0] ?? undefined;
	}
);

// Get Latest Underwriter No Decision Policy Note Reasons Selector.
const makeGetLatestUnderwriterNoDecisionPolicyNoteReasonsSelector = () => createSelector(
	[makeGetLatestUnderwriterNoDecisionPolicyNoteSelector()],
	(policyNote: PolicyNoteRecord | undefined): string[] => {
		let reasons: string[] = [];
		let note: string | undefined = policyNote?.note ?? undefined;

		if (note !== undefined) {
			let parsedValues = note.replaceAll("[", "(/L)");
			parsedValues = parsedValues.replaceAll("]", "(/R)");
			parsedValues = parsedValues.replaceAll("<li>", "[");
			parsedValues = parsedValues.replaceAll("</li>", "]");
			reasons = parsedValues.match(/(?<=\[)[^\[\]]+(?=\])/gmi)?.map((result) => result.replaceAll("(/L)", "[").replaceAll("(/R)", "]")) ?? [];
		}
		return reasons;
	}
);

// Get Policy Notes Count Selector.
const makeGetPolicyNotesCountSelector = () => createSelector(
	[makeGetPolicyNotesSelector()],
	(policyNotes: PolicyNoteRecord[]): number => {
		return policyNotes.length;
	}
);

// Get Policy Note Ids Selector.
const makeGetPolicyNoteIdsSelector = () => createSelector(
	[makeGetPolicyNotesSelector()],
	(policyNotes: PolicyNoteRecord[]): number[] => {
		return policyNotes.map((policyNote: PolicyNoteRecord) => {
			return policyNote.id !== null ? policyNote.id : -1;
		}).filter((policyNoteId: number) => {
			return policyNoteId !== -1;
		});
	}
);

// Get Selected Policy Note Id Selector.
const makeGetSelectedPolicyNoteIdSelector = () => createSelector(
	[getPoliciesState],
	(policyState: PolicyState | undefined): number | undefined => {
		return policyState !== undefined
			? policyState.selectedPolicyNoteId
			: undefined;
	}
);

// Get Selected Policy Note Selector.
const makeGetSelectedPolicyNoteSelector = () => createSelector(
	[makeGetPolicyNotesSelector(),  makeGetSelectedPolicyNoteIdSelector()],
	(policyNotes: PolicyNoteRecord[], selectedPolicyNoteId: number | undefined): PolicyNoteRecord | undefined => {
		return selectedPolicyNoteId !== undefined
			? policyNotes.find((policyNote: PolicyNoteRecord) => policyNote.id === selectedPolicyNoteId)
			: undefined;
	}
);

// Get Selected Policy Note Flag Date Selector.
const makeGetSelectedPolicyNoteFlagDateSelector = () => createSelector(
	[makeGetSelectedPolicyNoteSelector()],
	(selectedPolicyNote: PolicyNoteRecord | undefined): Date | undefined => {
		return selectedPolicyNote !== undefined && selectedPolicyNote.flagDate !== null
			? selectedPolicyNote.flagDate
			: undefined;
	}
);

// Are Policy Notes Loaded Selector.
const makeArePolicyNotesLoadedSelector = () => createSelector(
	[getPoliciesState],
	(policyState: PolicyState | undefined): boolean => {
		return policyState !== undefined
			? policyState.arePolicyNotesLoaded
			: false;
	}
);

// export selectors.
export {
	makeGetPolicyNotesSelector,
	makeGetUnderwriterDeclinedPolicyNotesSelector,
	makeGetUnderwriterHoldPolicyNotesSelector,
	makeGetUnderwriterNoDecisionPolicyNotesSelector,
	makeGetLatestUnderwriterNoDecisionPolicyNoteSelector,
	makeGetLatestUnderwriterNoDecisionPolicyNoteReasonsSelector,
	makeGetPolicyNotesCountSelector,
	makeGetPolicyNoteIdsSelector,
	makeGetSelectedPolicyNoteIdSelector,
	makeGetSelectedPolicyNoteSelector,
	makeGetSelectedPolicyNoteFlagDateSelector,
	makeArePolicyNotesLoadedSelector,
};